// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// TanStack Query hook for a site's anomalies (upstream AEM findings). Each flat
// record maps to the UI `Alarm` model via the shared alarm adapter — anomalies
// share the alarm record shape.

import { useQuery } from '@tanstack/react-query';
import { listAllAnomalies } from '../services/anomaliesApiService';
import { alarmApiRecordToAlarm } from '../services/alarmMappers';
import { queryKeys } from '../lib/queryKeys';
import type { Alarm } from '../types/alarm';

export interface AnomaliesOptions {
  /** Poll interval in ms. Opt-in — see ConsoleAlarmsOptions for the rationale. */
  pollIntervalMs?: number;
}

/** Fetch all of a site's anomalies, mapped to the UI `Alarm` model. */
export function useAnomalies(siteId?: string, { pollIntervalMs }: AnomaliesOptions = {}) {
  const query = useQuery({
    queryKey: queryKeys.anomalies(siteId),
    queryFn: async (): Promise<Alarm[]> => {
      const records = await listAllAnomalies(siteId as string);
      const items: Alarm[] = [];
      const seen = new Set<string>();
      for (const record of records) {
        const alarm = alarmApiRecordToAlarm(record);
        if (seen.has(alarm.uid)) continue;
        seen.add(alarm.uid);
        items.push(alarm);
      }
      return items;
    },
    enabled: !!siteId,
    refetchInterval: pollIntervalMs ?? false,
  });

  const items = query.data ?? [];
  return { ...query, items, count: items.length };
}
